import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function AccountingDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Data states
  const [ledgerAccounts, setLedgerAccounts] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [agentFloats, setAgentFloats] = useState([]);
  
  // Modal states
  const [showCapitalModal, setShowCapitalModal] = useState(false);
  
  // Form states
  const [capitalForm, setCapitalForm] = useState({ amount: "", description: "", investorName: "Owner" });
  
  // Filters
  const [dateFilter, setDateFilter] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const api = axios.create({
    baseURL: "/api",
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    loadLedgerAccounts(); // Load on mount to compute summary
    loadAgentFloats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === "ledger") loadLedgerAccounts();
    if (activeTab === "journals") loadJournalEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Compute financial summary from ledger accounts and agent floats
  const computedSummary = React.useMemo(() => {
    if (!ledgerAccounts || ledgerAccounts.length === 0) return null;
    
    // Get Cash (1000)
    const companyCash = ledgerAccounts.find(a => 
      a.accountCode === "1000" || a.accountNumber === "1000" || a.accountName === "Cash"
    )?.balance || 0;
    
    // Get Agent Float from agentFloats data (actual wallet balances)
    const totalAgentFloat = agentFloats.reduce((sum, agent) => 
      sum + (agent.balance || agent.walletBalance || 0), 0);
    
    // Get Customer Deposits (2000)
    const customerDeposits = ledgerAccounts.find(a => 
      a.accountCode === "2000" || a.accountNumber === "2000" || a.accountName === "Customer Deposits"
    )?.balance || 0;
    
    // Get ALL Equity accounts (3xxx) - Owner's Equity, Retained Earnings, Capital Account
    const totalEquityAccounts = ledgerAccounts
      .filter(a => a.accountType === "Equity")
      .reduce((sum, a) => sum + (a.balance || 0), 0);
    
    // Get ALL Revenue accounts (4xxx)
    const totalRevenue = ledgerAccounts
      .filter(a => a.accountType === "Revenue")
      .reduce((sum, a) => sum + (a.balance || 0), 0);
    
    // Get ALL Expense accounts (5xxx, 6xxx)
    const totalExpenses = ledgerAccounts
      .filter(a => a.accountType === "Expense")
      .reduce((sum, a) => sum + (a.balance || 0), 0);
    
    // Calculate totals using proper accounting equation:
    // Assets = Liabilities + Equity + Revenue - Expenses
    const totalAssets = companyCash + totalAgentFloat;
    const totalLiabilities = customerDeposits;
    const totalEquity = totalEquityAccounts;
    const netIncome = totalRevenue - totalExpenses;
    const totalLiabilitiesAndEquity = totalLiabilities + totalEquity + netIncome;
    
    return {
      assets: { companyCash, totalAgentFloat },
      liabilities: { customerDeposits },
      equity: { ownerEquity: totalEquityAccounts, totalRevenue, totalExpenses, netIncome },
      balanceCheck: {
        totalAssets,
        totalLiabilitiesAndEquity,
        isBalanced: Math.abs(totalAssets - totalLiabilitiesAndEquity) < 1
      }
    };
  }, [ledgerAccounts, agentFloats]);

  const loadLedgerAccounts = async () => {
    setLoading(true);
    try {
      const res = await api.get("/Accounting/ledger-accounts");
      setLedgerAccounts(res.data);
    } catch (err) {
      setError("Failed to load ledger accounts");
    } finally {
      setLoading(false);
    }
  };

  const loadJournalEntries = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/Accounting/journal-entries?startDate=${dateFilter.startDate}&endDate=${dateFilter.endDate}`);
      setJournalEntries(res.data);
    } catch (err) {
      setError("Failed to load journal entries");
    } finally {
      setLoading(false);
    }
  };

  const loadAgentFloats = async () => {
    try {
      const res = await api.get("/Accounting/agent-floats");
      setAgentFloats(res.data);
    } catch (err) {
      console.error("Failed to load agent floats");
    }
  };

  const initializeAccounts = async () => {
    try {
      await api.post("/Accounting/initialize");
      alert("Chart of accounts initialized!");
      loadLedgerAccounts();
    } catch (err) {
      alert("Failed to initialize accounts");
    }
  };

  const initializeFees = async () => {
    try {
      await api.post("/Fee/initialize");
      alert("Default fees initialized!");
    } catch (err) {
      alert("Failed to initialize fees");
    }
  };

  const recordCapitalInvestment = async () => {
    if (!capitalForm.amount || Number(capitalForm.amount) <= 0) {
      alert("Please enter a valid amount");
      return;
    }
    try {
      const res = await api.post("/Accounting/capital/invest", {
        amount: Number(capitalForm.amount),
        investorName: capitalForm.investorName,
        description: capitalForm.description
      });
      alert(res.data.message || "Capital recorded successfully!");
      setShowCapitalModal(false);
      setCapitalForm({ amount: "", description: "", investorName: "Owner" });
      loadLedgerAccounts();
    } catch (err) {
      alert("Failed to record capital");
    }
  };

  const formatCurrency = (amount) => {
    return `₦${(amount || 0).toLocaleString()}`;
  };

  const getTypeColor = (type) => {
    const colors = {
      Asset: "#28a745",
      Liability: "#dc3545",
      Equity: "#6f42c1",
      Revenue: "#007bff",
      Expense: "#ffc107"
    };
    return colors[type] || "#6c757d";
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📊 Accounting Dashboard</h1>
        <div style={styles.headerButtons}>
          <button onClick={() => navigate("/admin")} style={styles.btnOutline}>← Admin</button>
          <button onClick={() => navigate("/ledger-query")} style={styles.btnSecondary}>● Ledger Query</button>
          <button onClick={() => navigate("/trial-balance")} style={styles.btnPrimary}>📑 Trial Balance & P&L</button>
          <button onClick={() => navigate("/liquidity-planning")} style={styles.btnPrimary}>💧 Liquidity Planning</button>
          <button onClick={initializeAccounts} style={styles.btnSecondary}>Init Accounts</button>
          <button onClick={initializeFees} style={styles.btnSecondary}>Init Fees</button>
          <button onClick={() => setShowCapitalModal(true)} style={styles.btnSuccess}>💰 Add Capital</button>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Financial Summary */}
      <div style={styles.summarySection}>
        <div style={styles.cardGrid}>
          <div style={{...styles.summaryCard, borderLeft: "4px solid #28a745"}}>
            <div style={styles.cardLabel}>Company Cash</div>
            <div style={{...styles.cardValue, color: "#28a745"}}>{formatCurrency(computedSummary?.assets?.companyCash)}</div>
          </div>
          <div style={{...styles.summaryCard, borderLeft: "4px solid #007bff"}}>
            <div style={styles.cardLabel}>Agent Float (Total)</div>
            <div style={{...styles.cardValue, color: "#007bff"}}>{formatCurrency(computedSummary?.assets?.totalAgentFloat)}</div>
          </div>
          <div style={{...styles.summaryCard, borderLeft: "4px solid #dc3545"}}>
            <div style={styles.cardLabel}>Customer Deposits</div>
            <div style={{...styles.cardValue, color: "#dc3545"}}>{formatCurrency(computedSummary?.liabilities?.customerDeposits)}</div>
          </div>
          <div style={{...styles.summaryCard, borderLeft: "4px solid #6f42c1"}}>
            <div style={styles.cardLabel}>Owner's Equity</div>
            <div style={{...styles.cardValue, color: "#6f42c1"}}>{formatCurrency(computedSummary?.equity?.ownerEquity)}</div>
          </div>
        </div>
        
        {/* Balance Check */}
        <div style={{
          ...styles.balanceCheck,
          backgroundColor: computedSummary?.balanceCheck?.isBalanced ? "#d4edda" : "#f8d7da",
          color: computedSummary?.balanceCheck?.isBalanced ? "#155724" : "#721c24"
        }}>
          <span>Assets: {formatCurrency(computedSummary?.balanceCheck?.totalAssets)} | Liabilities + Equity: {formatCurrency(computedSummary?.balanceCheck?.totalLiabilitiesAndEquity)}</span>
          <span style={{ fontWeight: "bold" }}>
            {computedSummary?.balanceCheck?.isBalanced ? "✓ BALANCED" : "✗ UNBALANCED"}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button onClick={() => setActiveTab("overview")} style={{...styles.tab, ...(activeTab === "overview" ? styles.tabActive : {})}}>📊 Overview</button>
        <button onClick={() => setActiveTab("ledger")} style={{...styles.tab, ...(activeTab === "ledger" ? styles.tabActive : {})}}>📚 Chart of Accounts</button>
        <button onClick={() => setActiveTab("journals")} style={{...styles.tab, ...(activeTab === "journals" ? styles.tabActive : {})}}>📝 Journal Entries</button>
      </div>

      {loading && <div style={styles.loading}>Loading...</div>}

      {/* Overview Tab - Financial Summary */}
      {activeTab === "overview" && !loading && (
        <div style={styles.card}>
          <h3>📊 Financial Overview</h3>
          <p style={{color: "#666", marginBottom: "20px"}}>
            View detailed financial information in the Chart of Accounts and Journal Entries tabs.
            For Agent Float Management, please use the dedicated <strong>Agent Float</strong> module.
          </p>
          <div style={{display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "15px"}}>
            <button onClick={() => setActiveTab("ledger")} style={{...styles.btnSmall, padding: "15px", fontSize: "14px"}}>
              📚 View Chart of Accounts
            </button>
            <button onClick={() => setActiveTab("journals")} style={{...styles.btnSmall, padding: "15px", fontSize: "14px"}}>
              📝 View Journal Entries
            </button>
          </div>
        </div>
      )}

      {/* Chart of Accounts Tab */}
      {activeTab === "ledger" && !loading && (
        <div style={styles.card}>
          <h3>Chart of Accounts</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Account #</th>
                <th style={styles.th}>Account Name</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {ledgerAccounts.map((account, i) => (
                <tr key={i}>
                  <td style={styles.td}>{account.accountCode || account.accountNumber}</td>
                  <td style={styles.td}>{account.accountName}</td>
                  <td style={styles.td}>
                    <span style={{...styles.typeBadge, backgroundColor: getTypeColor(account.accountType), color: "white"}}>
                      {account.accountType}
                    </span>
                  </td>
                  <td style={styles.td}>{account.accountCategory}</td>
                  <td style={{...styles.td, fontWeight: "bold", textAlign: "right"}}>{formatCurrency(account.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Journal Entries Tab */}
      {activeTab === "journals" && !loading && (
        <div style={styles.card}>
          <div style={styles.filterRow}>
            <label>From:</label>
            <input type="date" value={dateFilter.startDate} onChange={(e) => setDateFilter({...dateFilter, startDate: e.target.value})} style={styles.dateInput} />
            <label>To:</label>
            <input type="date" value={dateFilter.endDate} onChange={(e) => setDateFilter({...dateFilter, endDate: e.target.value})} style={styles.dateInput} />
            <button onClick={loadJournalEntries} style={styles.btnSmall}>Filter</button>
          </div>
          {journalEntries.length === 0 ? (
            <p style={{textAlign: "center", color: "#666"}}>No journal entries found</p>
          ) : (
            journalEntries.map((entry, i) => (
              <div key={i} style={{marginBottom: "20px", padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "6px"}}>
                <div style={{display: "flex", justifyContent: "space-between", marginBottom: "10px"}}>
                  <strong>{entry.journalNumber}</strong>
                  <span style={{color: "#666", fontSize: "12px"}}>{new Date(entry.createdAt).toLocaleString()}</span>
                </div>
                <p style={{margin: "5px 0", color: "#333"}}>{entry.description}</p>
                <table style={{...styles.table, fontSize: "12px"}}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Account</th>
                      <th style={{...styles.th, textAlign: "right"}}>Debit</th>
                      <th style={{...styles.th, textAlign: "right"}}>Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entry.lines?.map((line, j) => (
                      <tr key={j}>
                        <td style={styles.td}>{line.accountCode} - {line.accountName}</td>
                        <td style={{...styles.td, textAlign: "right", color: "#28a745"}}>{line.debit ? formatCurrency(line.debit) : ""}</td>
                        <td style={{...styles.td, textAlign: "right", color: "#dc3545"}}>{line.credit ? formatCurrency(line.credit) : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </div>
      )}

      {/* Capital Investment Modal */}
      {showCapitalModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>💰 Record Capital Investment</h3>
            <div style={styles.formGroup}>
              <label>Amount (₦)</label>
              <input type="number" value={capitalForm.amount} onChange={(e) => setCapitalForm({...capitalForm, amount: e.target.value})} style={styles.input} placeholder="Enter amount" />
            </div>
            <div style={styles.formGroup}>
              <label>Investor Name</label>
              <input type="text" value={capitalForm.investorName} onChange={(e) => setCapitalForm({...capitalForm, investorName: e.target.value})} style={styles.input} placeholder="Owner" />
            </div>
            <div style={styles.formGroup}>
              <label>Description</label>
              <input type="text" value={capitalForm.description} onChange={(e) => setCapitalForm({...capitalForm, description: e.target.value})} style={styles.input} placeholder="Initial capital investment" />
            </div>
            <div style={styles.modalFooter}>
              <button onClick={() => setShowCapitalModal(false)} style={styles.btnOutline}>Cancel</button>
              <button onClick={recordCapitalInvestment} style={styles.btnPrimary}>Record Investment</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: "20px", maxWidth: "1200px", margin: "0 auto", backgroundColor: "#f5f5f5", minHeight: "100vh" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "10px" },
  title: { fontSize: "24px", margin: 0 },
  headerButtons: { display: "flex", gap: "10px", flexWrap: "wrap" },
  
  summarySection: { marginBottom: "20px" },
  cardGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "15px" },
  summaryCard: { backgroundColor: "white", padding: "15px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  cardLabel: { fontSize: "12px", color: "#666", marginBottom: "5px" },
  cardValue: { fontSize: "20px", fontWeight: "bold" },
  
  balanceCheck: { padding: "12px 15px", borderRadius: "6px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  
  tabs: { display: "flex", gap: "5px", marginBottom: "20px", flexWrap: "wrap" },
  tab: { padding: "10px 15px", border: "none", backgroundColor: "#e9ecef", borderRadius: "5px", cursor: "pointer", fontSize: "13px" },
  tabActive: { backgroundColor: "#007bff", color: "white" },
  
  card: { backgroundColor: "white", borderRadius: "8px", padding: "20px", marginBottom: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  
  table: { width: "100%", borderCollapse: "collapse", fontSize: "13px" },
  th: { textAlign: "left", padding: "10px 8px", borderBottom: "2px solid #dee2e6", backgroundColor: "#f8f9fa" },
  td: { padding: "10px 8px", borderBottom: "1px solid #dee2e6" },
  
  filterRow: { display: "flex", gap: "10px", alignItems: "center", marginBottom: "15px", flexWrap: "wrap" },
  dateInput: { padding: "8px", border: "1px solid #ddd", borderRadius: "4px" },
  
  btnPrimary: { padding: "8px 15px", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "13px" },
  btnSecondary: { padding: "8px 15px", backgroundColor: "#6c757d", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "13px" },
  btnSuccess: { padding: "8px 15px", backgroundColor: "#28a745", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "13px" },
  btnOutline: { padding: "8px 15px", backgroundColor: "white", color: "#007bff", border: "1px solid #007bff", borderRadius: "4px", cursor: "pointer", fontSize: "13px" },
  btnSmall: { padding: "6px 12px", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px" },
  btnSmallGreen: { padding: "5px 10px", backgroundColor: "#28a745", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "11px", marginRight: "5px" },
  btnSmallRed: { padding: "5px 10px", backgroundColor: "#dc3545", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "11px" },
  
  badge: { padding: "3px 8px", backgroundColor: "#28a745", color: "white", borderRadius: "10px", fontSize: "11px" },
  typeBadge: { padding: "3px 8px", borderRadius: "4px", fontSize: "11px" },
  
  error: { backgroundColor: "#f8d7da", color: "#721c24", padding: "10px", borderRadius: "4px", marginBottom: "15px" },
  loading: { textAlign: "center", padding: "40px", color: "#666" },
  
  // Modal styles
  modalOverlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { backgroundColor: "white", borderRadius: "8px", padding: "25px", width: "400px", maxWidth: "90%" },
  modalTitle: { marginTop: 0, marginBottom: "20px" },
  formGroup: { marginBottom: "15px" },
  input: { width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px", boxSizing: "border-box" },
  modalFooter: { display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "20px" }
};

